import React, { useState } from "react"

import { addWeeks, startOfWeek, subWeeks } from "date-fns"

import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Columns2,
  LayoutList,
  Lock,
  Unlock,
} from "lucide-react"

import { LoadingState } from "@/components/shared/LoadingState"
import { PageHeader } from "@/components/shared/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { formatDayMonth, formatDayMonthYear, formatDayNumeric } from "@/lib/dateFormats"
import { RouteWeeklyData } from "@/lib/weeklyPlan"

import DeltaStrip from "./weekly/DeltaStrip"
import RouteDetailSheet, { RouteDetailSelection } from "./weekly/RouteDetailSheet"
import WeekPanel from "./weekly/WeekPanel"
import WeekTable from "./weekly/WeekTable"
import { useWeekData } from "./weekly/useWeekData"

type ViewMode = "single" | "compare"

const WeeklyPlanView: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>("single")
  const [selectedWeekStart, setSelectedWeekStart] = useState<Date>(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 }),
  )
  const [compareWeekStart, setCompareWeekStart] = useState<Date>(() =>
    addWeeks(startOfWeek(new Date(), { weekStartsOn: 1 }), 1),
  )
  const [routeDetail, setRouteDetail] = useState<RouteDetailSelection | null>(null)

  const {
    weekEnd,
    weekDays,
    data: singleWeekData,
    isLoading: singleIsLoading,
  } = useWeekData(selectedWeekStart)
  const { weekDays: compareWeekDays, data: compareWeekData } = useWeekData(compareWeekStart)

  const handleRouteClick = (route: RouteWeeklyData, dateStr: string, dayLabel: string) => {
    setRouteDetail({ route, dateStr, dayLabel })
  }

  const goToPrevWeek = () => setSelectedWeekStart((prev) => subWeeks(prev, 1))
  const goToNextWeek = () => setSelectedWeekStart((prev) => addWeeks(prev, 1))
  const goToCurrentWeek = () => setSelectedWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))

  if (viewMode === "compare") {
    return (
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <PageHeader
          icon={<Calendar className="h-5 w-5 text-palette-jungleGreen500" />}
          title="Compare Weekly Plans"
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setViewMode("single")}
              >
                <LayoutList className="h-3.5 w-3.5" />
                Single Week
              </Button>
              <Button
                variant="default"
                size="sm"
                className="gap-1.5"
                onClick={() => setViewMode("compare")}
              >
                <Columns2 className="h-3.5 w-3.5" />
                Compare Weeks
              </Button>
            </div>
          }
        />
        <div className="flex-1 min-h-0 overflow-auto px-6 py-6 pb-8 space-y-6">
          <WeekPanel
            weekStart={selectedWeekStart}
            onWeekChange={setSelectedWeekStart}
            compact
            compactSummary
            defaultCollapsed
            onRouteClick={handleRouteClick}
          />
          <div className="border-t border-palette-jungleGreen200" />
          <WeekPanel
            weekStart={compareWeekStart}
            onWeekChange={setCompareWeekStart}
            compact
            compactSummary
            defaultCollapsed
            onRouteClick={handleRouteClick}
          />
          <DeltaStrip
            leftData={singleWeekData}
            rightData={compareWeekData}
            leftWeekDays={weekDays}
            rightWeekDays={compareWeekDays}
          />
        </div>
        <RouteDetailSheet selection={routeDetail} onClose={() => setRouteDetail(null)} />
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      <PageHeader
        icon={<Calendar className="h-5 w-5 text-palette-jungleGreen500" />}
        title={`Plan WC ${formatDayNumeric(selectedWeekStart)}`}
        meta={
          <>
            <span className="text-sm text-palette-jungleGreen500">
              {formatDayMonth(selectedWeekStart)} — {formatDayMonthYear(weekEnd)}
            </span>
            {singleWeekData.isLocked && (
              <Badge variant="secondary" className="gap-1 text-xs">
                <Lock className="h-3 w-3" />
                Locked
              </Badge>
            )}
            {!singleWeekData.isLocked && !singleIsLoading && (
              <Badge variant="outline" className="gap-1 text-xs text-blue-600 border-blue-300">
                <Unlock className="h-3 w-3" />
                Live
              </Badge>
            )}
          </>
        }
        actions={
          <>
            <div className="flex items-center gap-1">
              <Button
                variant="default"
                size="sm"
                className="gap-1.5"
                onClick={() => setViewMode("single")}
              >
                <LayoutList className="h-3.5 w-3.5" />
                Single Week
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setViewMode("compare")}
              >
                <Columns2 className="h-3.5 w-3.5" />
                Compare Weeks
              </Button>
            </div>
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" onClick={goToPrevWeek}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToCurrentWeek}>
                Today
              </Button>
              <Button variant="outline" size="sm" onClick={goToNextWeek}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </>
        }
      />

      <div className="flex-1 min-h-0 overflow-auto px-6 py-6 pb-8">
        {singleIsLoading ? (
          <LoadingState label="Loading weekly plan…" />
        ) : (
          <WeekTable weekDays={weekDays} data={singleWeekData} onRouteClick={handleRouteClick} />
        )}
      </div>
      <RouteDetailSheet selection={routeDetail} onClose={() => setRouteDetail(null)} />
    </div>
  )
}

export default WeeklyPlanView
